/**
 * AssetManager — loads/owns the editor's assets as Babylon `Texture`/`VideoTexture`
 * (registerAssets/getAsset/mute/unmute/cleanup) via the EditorAsset state machine.
 *
 * Texture/video construction adapts eyejack-web-core's core/loader/loader.ts
 * (loadTexture/loadVideo). Asset paths arrive already rewritten to the dev
 * proxy by the shared loader, so no CDN rewrite is needed here.
 */

import { Texture, VideoTexture, type Scene, type BaseTexture, type AssetContainer } from 'babylonjs';
// GLB load/cache/instancing lives in web-core (shared rendering between the editor
// and the boilerplates). getModelContainer loads a GLB into a per-scene cached
// AssetContainer template (deduped by URL); disposeModelCache tears the templates
// down. babylonjs-loaders is registered by that module.
import { getModelContainer, disposeModelCache } from '@webcore/core/loader/loader.modelinstance';
// Shared texture loader (web-core) — the editor delegates the Babylon Texture
// creation and keeps its own retry/registry/notify around it.
import { loadTexture, type IAssetTexture } from '@webcore/core/loader/loader';
import { AssetTypes, type AssetType } from '@shared/constants';
import { textureFromBase64 } from '../utils/BabylonEditorUtils';
import { renderModelThumbnail } from '@webcore/core/utils/renderModelThumbnail';
import type { EditorState } from '@shared/EditorState';
import type { EventBus } from '@shared/events/EventBus';
import type { EditorEvents } from '@shared/events/EventTypes';
import type { AssetDefinition } from '@shared/types';
import FallbackTexture from '../../../assets/fallback_texture_alt.png';

export class AssetLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AssetLoadError';
  }
}

/** A single loaded (or loading) Babylon asset. */
export class EditorAsset {
  assetID: string;
  assetType: AssetType | string;
  assetPath: string;
  width: number;
  height: number;
  isLoaded = false;
  texture: BaseTexture | null = null;
  videoElement: HTMLVideoElement | null = null;
  // GLB assets load into an AssetContainer template; each element instantiates
  // its own independent copy from it (so the same GLB can back many elements).
  container: AssetContainer | null = null;
  private loadedCallbacks: Array<() => void> = [];

  constructor(assetID: string, assetType: AssetType | string, assetPath: string, width: number, height: number) {
    this.assetID = assetID;
    this.assetType = assetType;
    this.assetPath = assetPath;
    this.width = width;
    this.height = height;
  }

  notifyLoaded(): void {
    this.isLoaded = true;
    this.loadedCallbacks.forEach((cb) => cb());
    this.loadedCallbacks = [];
  }

  addLoadedCallback(callback: () => void): void {
    this.loadedCallbacks.push(callback);
  }

  cleanup(): void {
    this.texture?.dispose();
    if (this.videoElement) {
      this.videoElement.parentNode?.removeChild(this.videoElement);
      if (this.videoElement.src?.startsWith('blob:')) URL.revokeObjectURL(this.videoElement.src);
    }
    // NOTE: the GLB template is owned by web-core's per-scene model cache (this is
    // just a reference to it), so it is NOT disposed here — AssetManager.cleanup
    // calls disposeModelCache(scene) once. Per-element instances are disposed by
    // their own ModelElement.cleanup().
    this.container = null;
    this.videoElement = null;
    this.texture = null;
  }
}

export class AssetManager {
  assets: EditorAsset[] = [];
  private scene: Scene;
  private state: EditorState;
  private events: EventBus<EditorEvents>;
  private videosToUnlock: HTMLVideoElement[] = [];
  private isUnlockListenerActive = false;
  private notifyTimeout: ReturnType<typeof setTimeout> | null = null;
  private videoContainer: HTMLElement | null = null;

  constructor(scene: Scene, state: EditorState, events: EventBus<EditorEvents>) {
    this.scene = scene;
    this.state = state;
    this.events = events;
    this.unlockNextVideo = this.unlockNextVideo.bind(this);
  }

  private getVideoContainer(): HTMLElement {
    if (!this.videoContainer) {
      this.videoContainer = document.createElement('div');
      this.videoContainer.id = 'editor-video-helper';
      this.videoContainer.style.display = 'none';
      document.body.appendChild(this.videoContainer);
    }
    return this.videoContainer;
  }

  registerAssets(definitions: AssetDefinition[]): void {
    for (const def of definitions) {
      if (this.assets.some((a) => a.assetID === def.assetID)) continue;
      const asset = new EditorAsset(def.assetID, def.assetType, def.assetPath, def.width, def.height);
      this.assets.push(asset);
      this._loadAsset(asset);
    }
  }

  getAsset(assetID: string): Promise<EditorAsset | null> {
    const asset = this.assets.find((a) => a.assetID === assetID);
    if (!asset) return Promise.resolve(null);
    if (asset.isLoaded) return Promise.resolve(asset);
    return new Promise((resolve) => asset.addLoadedCallback(() => resolve(asset)));
  }

  /**
   * Synchronous asset-type lookup. registerAssets() always runs before an
   * element is constructed, so EditorScene can use this to pick the right
   * element class (panel vs 3D model) up front, even while the asset is still
   * loading.
   */
  getAssetType(assetID: string): string | undefined {
    return this.assets.find((a) => a.assetID === assetID)?.assetType;
  }

  private async _loadAsset(asset: EditorAsset): Promise<void> {
    try {
      if (asset.assetType === AssetTypes.VIDEO_OPAQUE || asset.assetType === AssetTypes.VIDEO_ALPHA) {
        await this._loadVideoAsset(asset);
      } else if (asset.assetType === AssetTypes.GLB) {
        await this._loadModelAsset(asset);
      } else {
        await this._loadImageAsset(asset);
      }
    } catch (error) {
      // GLB can't fall back to a 2D texture (the element is a ModelElement that
      // expects a container). Mark it loaded with no container so ModelElement
      // renders its placeholder box; surface the error to the user.
      if (asset.assetType === AssetTypes.GLB) {
        console.warn(`Editor: GLB ${asset.assetID} failed to load.`, error);
        asset.container = null;
        asset.notifyLoaded();
        this.notifyErrors(error instanceof Error ? error : new Error(String(error)));
        return;
      }
      console.warn(`Editor: Asset ${asset.assetID} failed to load. Loading fallback.`);
      try {
        asset.texture = await textureFromBase64(FallbackTexture, this.scene);
        asset.assetType = AssetTypes.IMAGE;
        asset.notifyLoaded();
      } catch {
        this.notifyErrors(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }

  /**
   * Load a GLB into an AssetContainer template (NOT added to the scene). The
   * `.glb` plugin-extension hint lets SceneLoader parse a `data:`/blob URL that
   * carries no file extension. Each ModelElement instantiates its own copy.
   */
  private async _loadModelAsset(asset: EditorAsset): Promise<void> {
    // Load (or reuse) the GLB template via web-core's shared per-scene cache. The
    // `.glb` extension hint lets it parse a data:/blob/proxied URL with no file
    // extension. Each ModelElement instantiates its own copy from this template.
    const container = await getModelContainer(asset.assetPath, this.scene, '.glb');
    asset.container = container;
    asset.notifyLoaded();
    this.events.emit('asset:loaded', { assetId: asset.assetID });

    // Render a one-shot preview thumbnail for the asset list (fire-and-forget;
    // reuses the same thumbnails:generated channel as VIDEO_ALPHA).
    this._generateModelThumbnail(asset);
  }

  private async _generateModelThumbnail(asset: EditorAsset): Promise<void> {
    if (!asset.container) return; // only for successfully-loaded GLBs
    try {
      // Rendered in an isolated offscreen scene loaded from the asset path, so
      // it can't touch the live model's shared materials/textures.
      const dataUrl = await renderModelThumbnail(this.scene, asset.assetPath);
      if (dataUrl) {
        this.events.emit('thumbnails:generated', { thumbnails: { [asset.assetID]: dataUrl } });
      }
    } catch (e) {
      console.warn(`Editor: could not generate thumbnail for ${asset.assetID}:`, e);
    }
  }

  private async _loadImageAsset(asset: EditorAsset, attempt = 3): Promise<void> {
    // Delegate the Babylon Texture creation to web-core's shared loader; keep the
    // editor's retry + registry/notify around it. Params match the editor's
    // panels: mipmapped, invertY (upright), trilinear, alpha.
    const assetT: IAssetTexture = { type: 'texture', path: asset.assetPath, id: asset.assetID };
    try {
      await loadTexture(assetT, this.scene, {
        noMipmap: false,
        invertY: true,
        samplingMode: Texture.TRILINEAR_SAMPLINGMODE,
        hasAlpha: true,
      });
      asset.texture = assetT.texture ?? null;
      asset.notifyLoaded();
      this.events.emit('asset:loaded', { assetId: asset.assetID });
    } catch (e) {
      if (attempt > 0) {
        return this._loadImageAsset(asset, attempt - 1);
      }
      throw new Error(`Failed to load image ${asset.assetID}: ${e instanceof Error ? e.message : e}`);
    }
  }

  private _loadVideoAsset(asset: EditorAsset, attempt = 3): Promise<void> {
    return new Promise((resolve, reject) => {
      const container = this.getVideoContainer();
      const videoElement = document.createElement('video');
      videoElement.loop = true;
      videoElement.playsInline = true;
      videoElement.setAttribute('playsinline', '');
      videoElement.muted = true; // start muted for autoplay
      videoElement.autoplay = true;
      videoElement.crossOrigin = 'anonymous';

      videoElement.addEventListener('loadeddata', () => {
        const playPromise = videoElement.play();
        if (playPromise !== undefined) playPromise.catch((e) => console.warn('Video autoplay prevented:', e));

        // invertY=false: video frames are top-left origin, so this renders upright
        // under the unlit/alpha shaders (matches the image-texture orientation).
        const videoTexture = new VideoTexture('video', videoElement, this.scene, false, false, undefined, {
          autoPlay: true,
          autoUpdateTexture: true,
        });
        asset.texture = videoTexture;
        asset.videoElement = videoElement;

        if (!this.state.muted) this.queueVideoForUnlock(videoElement);

        asset.notifyLoaded();
        this.events.emit('asset:loaded', { assetId: asset.assetID });
        resolve();
      });

      videoElement.addEventListener('error', (error) => {
        if (videoElement.parentNode === container) container.removeChild(videoElement);
        if (attempt > 0) {
          this._loadVideoAsset(asset, attempt - 1).then(resolve).catch(reject);
        } else {
          reject(error instanceof Error ? error : new Error(`Failed to load video ${asset.assetID}`));
        }
      });

      container.appendChild(videoElement);
      videoElement.src = asset.assetPath;
      videoElement.load();
    });
  }

  queueVideoForUnlock(videoElement: HTMLVideoElement): void {
    if (videoElement && !this.videosToUnlock.includes(videoElement)) {
      this.videosToUnlock.push(videoElement);
      if (!this.isUnlockListenerActive) {
        this.isUnlockListenerActive = true;
        window.addEventListener('pointerdown', this.unlockNextVideo);
      }
    }
  }

  private unlockNextVideo(): void {
    if (this.videosToUnlock.length === 0) {
      window.removeEventListener('pointerdown', this.unlockNextVideo);
      this.isUnlockListenerActive = false;
      return;
    }
    const video = this.videosToUnlock.shift();
    if (video) {
      try {
        video.muted = false;
        const playPromise = video.play();
        if (playPromise !== undefined) playPromise.catch((e) => console.warn('Video play failed after unlock:', e));
      } catch (error) {
        console.error('Failed to unlock video:', error);
      }
    }
  }

  /** Mute/unmute video assets. Assets not used in the current scene stay muted. */
  setAssetsMuted(muted: boolean): void {
    const elements = this.state.elements;
    this.assets.forEach((asset) => {
      if (asset.videoElement) {
        const usedInScene = elements.some((el) => el.assetID === asset.assetID);
        asset.videoElement.muted = muted || !usedInScene;
      }
    });
  }

  mute(): void {
    this.setAssetsMuted(true);
  }

  unmute(): void {
    this.setAssetsMuted(false);
  }

  notifyErrors(error: Error): void {
    if (this.notifyTimeout) clearTimeout(this.notifyTimeout);
    this.notifyTimeout = setTimeout(() => {
      const description = error.name === 'AssetLoadError'
        ? 'Try re-opening the project or re-uploading the missing assets.'
        : '';
      this.events.emit('asset:error', { message: error.message, description });
    }, 500);
  }

  cleanup(): void {
    this.assets.forEach((asset) => asset.cleanup());
    this.assets = [];
    // Dispose the shared GLB templates (+ their materials) for this scene, AFTER
    // every per-element instance has been disposed by ModelElement.cleanup().
    disposeModelCache(this.scene);
    window.removeEventListener('pointerdown', this.unlockNextVideo);
    this.videosToUnlock = [];
    this.isUnlockListenerActive = false;
    if (this.notifyTimeout) {
      clearTimeout(this.notifyTimeout);
      this.notifyTimeout = null;
    }
    if (this.videoContainer?.id === 'editor-video-helper' && this.videoContainer.parentNode) {
      this.videoContainer.parentNode.removeChild(this.videoContainer);
      this.videoContainer = null;
    }
  }
}

export default AssetManager;
